/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import type { ThemeType } from "@packages/ui";
import { ModalItem, ModalTab, useDynamicViewport } from "@packages/ui";
import type {
  CSSProperties,
  EventHandler,
  ForwardedRef,
  PropsWithChildren,
} from "react";
import { forwardRef, Fragment } from "react";

import { BIBLE_DESKTOP_WIDTH } from "../bibleStyle.constants";

const selectedStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.neutralsLowest,
});

type BibleNavItemProps = PropsWithChildren<{
  id: string;
  onItemClick: EventHandler<any>;
  selected: boolean;
  styleOverride?: CSSProperties;
  targetTabValue: string;
  className?: string;
}>;

export const BibleNavItem = forwardRef<HTMLDivElement, BibleNavItemProps>(
  (
    {
      id,
      onItemClick,
      selected,
      className = "",
      styleOverride,
      children,
      targetTabValue,
    },
    ref,
  ) => {
    const isDesktop = useDynamicViewport({ minimumWidth: BIBLE_DESKTOP_WIDTH });

    if (isDesktop) {
      return (
        <ModalItem
          key={`${id}_option`}
          onSelect={onItemClick}
          selected={selected}
          className={className}
          id={id}
          style={styleOverride}
          ref={ref as unknown as ForwardedRef<HTMLButtonElement>}
        >
          <Fragment key={`${id}_text`}>{children}</Fragment>
        </ModalItem>
      );
    }

    return (
      <ModalTab
        value={targetTabValue}
        key={`${id}_option`}
        onClick={onItemClick}
        css={selected ? selectedStyle : null}
        className={className}
        id={id}
        style={styleOverride}
        ref={ref}
      >
        <Fragment key={`${id}_text`}>{children}</Fragment>
      </ModalTab>
    );
  },
);

BibleNavItem.displayName = "BibleNavItem";
